import React, { useEffect, useState, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useSAPDataStore } from '../store/SAPDataStore';
import { useUnifiedAuth } from '../auth/useUnifiedAuth';
import { getValidToken } from '../auth/tokenManager';
import { natsClient, initializeNATS } from '../services/NATSClient';
import { orgNatsClient } from '../services/OrgNATSClient';
import { NavigationHistoryService } from '../services/NavigationHistoryService';
import { connectionHealthService } from '../services/ConnectionHealthService';
import { BackendDataService } from '../services/BackendDataService';

interface SAPDataProviderProps {
  children: React.ReactNode;
}

/**
 * Provider component that initializes SAP data when authentication is available
 */
export const SAPDataProvider: React.FC<SAPDataProviderProps> = ({ children }) => {
  const auth = useUnifiedAuth();
  const { token, authenticated, initialized } = auth;
  const fetchSAPData = useSAPDataStore(state => state.fetchSAPData);
  const setPendingData = useSAPDataStore(state => state.setPendingData);
  const applyPendingData = useSAPDataStore(state => state.applyPendingData);
  const hasPendingData = useSAPDataStore(state => state.hasPendingData);
  const existingMachines = useSAPDataStore(state => state.machines);
  const reset = useSAPDataStore(state => state.reset);
  const [hasTried, setHasTried] = useState(false);
  const [keycloakReady, setKeycloakReady] = useState(false);
  const [natsReady, setNatsReady] = useState(false);
  const [natsInitialized, setNatsInitialized] = useState(false);
  const location = useLocation();
  const locationRef = useRef(location.pathname);
  const existingMachinesRef = useRef(existingMachines);

  // Keep refs updated
  useEffect(() => {
    locationRef.current = location.pathname;
  }, [location.pathname]);

  useEffect(() => {
    existingMachinesRef.current = existingMachines;
  }, [existingMachines]);

  // Listen for Keycloak ready event
  useEffect(() => {
    const handleKeycloakReady = () => {
      setKeycloakReady(true);
    };
    
    window.addEventListener('keycloak-ready', handleKeycloakReady);
    return () => window.removeEventListener('keycloak-ready', handleKeycloakReady);
  }, []);

  // Set keycloakReady when already authenticated (event might have fired before listener)
  useEffect(() => {
    if (authenticated && initialized && !keycloakReady) {
      setKeycloakReady(true);
    }
  }, [authenticated, initialized, keycloakReady]);

  // Initialize NATS connection with authentication token
  useEffect(() => {
    const initializeNATSWithAuth = async () => {
      if (authenticated && token && !natsInitialized) {
        try {
          const natsServer = import.meta.env.VITE_NATS_URL || 'wss://nats.enlixy.com:443';
          await initializeNATS([natsServer], token);
          
          // Initialize NavigationHistoryService after NATS is connected
          await NavigationHistoryService.initialize();
          
          // Initialize TargetManagementService after NATS is connected
          const { TargetManagementService } = await import('../services/TargetManagementService');
          await TargetManagementService.initialize();
          
          // Start connection health monitoring
          connectionHealthService.startHealthChecks(15000); // Check every 15 seconds (faster detection)
          
          // Send prewarm request to backend only on fresh login (not page refresh)
          if (!sessionStorage.getItem('nats-prewarmed')) {
            orgNatsClient.sendPrewarmRequest(token);
            sessionStorage.setItem('nats-prewarmed', '1');
          }
          
          setNatsInitialized(true);
        } catch (error) {
          console.error('SAPDataProvider: NATS initialization failed:', error);
          // Retry after delay
          setTimeout(() => {
            setNatsInitialized(false);
          }, 5000);
        }
      } else if (authenticated && token && natsInitialized) {
        // Token refreshed while NATS is already connected — just update stored token
        natsClient.updateToken(token);
      } else if (!authenticated) {
        // Reset NATS initialization status when user logs out
        connectionHealthService.stopHealthChecks();
        setNatsInitialized(false);
      }
    };

    initializeNATSWithAuth();
  }, [authenticated, token, natsInitialized]);

  // Monitor NATS connection status
  // Monitor NATS connection status (with less frequent checks)
  useEffect(() => {
    const checkNATSStatus = () => {
      const isConnected = natsClient.isConnected();
      setNatsReady(isConnected);
    };

    // Check initial status
    checkNATSStatus();

    // Set up interval to check NATS status (every 10 seconds, less aggressive)
    const interval = setInterval(checkNATSStatus, 10000);
    
    return () => clearInterval(interval);
  }, [natsInitialized]); // Depend on natsInitialized to check status after initialization

  // Enhanced data fetching with store-aware logic
  useEffect(() => {
    // Only fetch SAP data if:
    // 1. All auth conditions are met AND
    // 2. NATS is properly initialized AND connected AND
    // 3. Haven't tried yet
    const shouldFetchData = initialized && 
                           keycloakReady && 
                           authenticated && 
                           token && 
                           natsInitialized &&
                           natsReady && 
                           !hasTried;

    console.log('🔍 SAPDataProvider check:', { 
      initialized, keycloakReady, authenticated, 
      hasToken: !!token, natsInitialized, natsReady, hasTried, 
      shouldFetchData 
    });

    if (shouldFetchData) {
      console.log('🔍 SAPDataProvider: Fetching SAP data...');
      fetchSAPData(token, true).catch(error => {
        console.error('SAPDataProvider: SAP data fetch failed:', error);
        // Reset hasTried to allow retry after delay
        setTimeout(() => {
          setHasTried(false);
        }, 10000); // Retry after 10 seconds
      });
      
      setHasTried(true);
    } else if (!authenticated) {
      reset();
      setHasTried(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, keycloakReady, authenticated, token, natsInitialized, natsReady, hasTried]);

  // Listen for manual refresh requests and connection recovery
  useEffect(() => {
    const handleManualRefresh = () => {
      if (authenticated && token && natsInitialized && natsReady) {
        setHasTried(false); // Allow refetch
      }
    };

    const handleConnectionRecovered = () => {
      console.log('🔄 SAPDataProvider: Connection recovered, refreshing data...');
      if (authenticated && token && natsInitialized) {
        setNatsReady(true);
        setHasTried(false); // Allow refetch
      }
    };

    const handleNATSReconnected = () => {
      console.log('🔄 SAPDataProvider: NATS reconnected, refreshing data...');
      if (authenticated && token && natsInitialized) {
        setNatsReady(true);
        setHasTried(false); // Allow refetch
      }
    };

    // Listen for manual refresh events
    window.addEventListener('sap-data-manual-refresh', handleManualRefresh);
    window.addEventListener('connection-recovered', handleConnectionRecovered);
    window.addEventListener('nats-reconnected', handleNATSReconnected);
    
    return () => {
      window.removeEventListener('sap-data-manual-refresh', handleManualRefresh);
      window.removeEventListener('connection-recovered', handleConnectionRecovered);
      window.removeEventListener('nats-reconnected', handleNATSReconnected);
      // Stop health checks when component unmounts
      connectionHealthService.stopHealthChecks();
    };
  }, [authenticated, token, natsInitialized, natsReady]);

  // Background sync every 5 minutes to keep machine data fresh
  useEffect(() => {
    if (!authenticated || !token || !natsInitialized || !natsReady) {
      return;
    }

    const SYNC_INTERVAL = 5 * 60 * 1000; // 5 minutes
    
    const backgroundSync = async () => {
      const currentPath = locationRef.current;
      const isOnOverview = currentPath === '/' || currentPath === '/overview';
      
      try {
        // Get a fresh token (refreshes if needed)
        const freshToken = await getValidToken();
        
        // Fetch data silently
        const newData = await BackendDataService.fetchSAPData(freshToken);
        
        // Ignore empty/failed responses (likely token error)
        if (!newData || Object.keys(newData).length === 0) {
          return;
        }
        
        const newMachines = BackendDataService.transformSAPDataToMachines(newData);
        const existingMachines = existingMachinesRef.current;
        
        // Ignore if new data is empty but we have existing data (likely a failed request)
        if (newMachines.length === 0 && existingMachines.length > 0) {
          return;
        }
        
        // Compare: check if machine count or any machine availability changed
        const hasChanges = 
          newMachines.length !== existingMachines.length ||
          newMachines.some(m => {
            const existing = existingMachines.find(e => e.maschinennummer === m.maschinennummer);
            return !existing || existing.datenVerfuegbar !== m.datenVerfuegbar;
          });
        
        if (hasChanges) {
          if (isOnOverview) {
            // On overview page, apply changes immediately
            console.log('🔄 Background sync: Data changed, updating (overview page)...');
            await fetchSAPData(freshToken, true);
          } else {
            // Not on overview, store as pending (no visual refresh)
            console.log('🔄 Background sync: Data changed, storing as pending...');
            setPendingData(newData);
          }
        }
        // If no changes: do nothing
      } catch (error) {
        // Silently ignore errors (token refresh, network issues, etc.)
      }
    };

    const intervalId = setInterval(backgroundSync, SYNC_INTERVAL);
    
    return () => clearInterval(intervalId);
  }, [authenticated, natsInitialized, natsReady, fetchSAPData, setPendingData]);

  // Apply pending data when navigating to overview
  useEffect(() => {
    const isOnOverview = location.pathname === '/' || location.pathname === '/overview';
    if (isOnOverview && hasPendingData) {
      applyPendingData();
    }
  }, [location.pathname, hasPendingData, applyPendingData]);

  return <>{children}</>;
};

export default SAPDataProvider;
